import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import type { PaginationMeta } from '@trellis/shared'
import { useState, type Dispatch, type SetStateAction } from 'react'

interface PagedInfiniteQueryPage {
  pagination?: Pick<PaginationMeta, 'total'> | null
}

type PagedInfiniteQuery<TPage extends PagedInfiniteQueryPage> = Pick<
  UseInfiniteQueryResult<InfiniteData<TPage>>,
  'data' | 'hasNextPage' | 'fetchNextPage'
>

export interface PagedInfiniteQueryState<TPage> {
  pageIndex: number
  setPageIndex: Dispatch<SetStateAction<number>>
  currentPageData: TPage | null
  totalPages: number
  hasNextPage: boolean
  goToNextPage: () => void
  goToPrevPage: () => void
}

/**
 * Derives page-by-page navigation state from a TanStack `useInfiniteQuery` result.
 * `totalPages` falls back to the fetched page count when the backend omits
 * `pagination.total` (endpoints that don't compute a count).
 */
export function usePagedInfiniteQuery<TPage extends PagedInfiniteQueryPage>(
  query: PagedInfiniteQuery<TPage>,
  limit: number,
): PagedInfiniteQueryState<TPage> {
  const [pageIndex, setPageIndex] = useState(0)

  const fetchedPageCount = query.data?.pages.length ?? 1
  const total = query.data?.pages[0]?.pagination?.total
  const totalPages =
    total !== undefined ? Math.max(1, Math.ceil(total / limit)) : fetchedPageCount
  const currentPageData = query.data?.pages[pageIndex] ?? null
  const isOnLastFetchedPage = pageIndex === fetchedPageCount - 1
  const hasNextPage = isOnLastFetchedPage ? (query.hasNextPage ?? false) : true

  function goToNextPage() {
    if (!hasNextPage) return
    if (isOnLastFetchedPage) void query.fetchNextPage()
    setPageIndex((previous) => previous + 1)
  }

  function goToPrevPage() {
    setPageIndex((previous) => Math.max(0, previous - 1))
  }

  return {
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  }
}
